<template>
  <v-layout row align-center style="width: 100%; overflow-x: hidden">
    <!-- Field Update Pending Approval -->
    <v-flex v-if="valValue && valValue.fieldUpdate && valValue.fieldUpdate.status === 'pending_approval'" xs12 class="body-1 py-1 text--primary">
      <div class="field-update-container">
        <div class="caption grey--text mb-2">
          <v-icon small color="amber" class="mr-1">pending</v-icon>
          AI Suggested Field Updates - Pending Approval
        </div>

        <div class="pending-updates-list">
          <div
            v-for="(update, idx) in valValue.fieldUpdate.pendingUpdates"
            :key="idx"
            class="pending-update-item pa-2 mb-2"
          >
            <div class="c-d-flex c-align-center c-justify-space-between">
              <div>
                <div class="body-2 font-weight-bold">{{ update.fieldName }}</div>
                <div class="caption grey--text">{{ update.parentStepName }} &bull; {{ update.fieldType }}</div>
              </div>
            </div>
            <div class="mt-2">
              <div class="caption grey--text">Proposed Value:</div>
              <div class="body-1 proposed-value pa-1" v-html="formatValue(update.value, update.fieldType)"></div>
            </div>
            <div v-if="update.reason" class="mt-1">
              <div class="caption grey--text text--darken-1">
                <v-icon x-small class="mr-1">info</v-icon>
                {{ update.reason }}
              </div>
            </div>
          </div>
        </div>

        <div class="pt-2 c-d-flex c-justify-space-between">
          <v-btn
            flat
            small
            color="error"
            class="ma-0"
            :disabled="approving"
            @click="rejectUpdates"
          >
            <v-icon left small>close</v-icon>
            Reject All
          </v-btn>
          <v-btn
            small
            color="secondary"
            class="ma-0"
            :loading="approving"
            :disabled="approving"
            @click="approveUpdates"
          >
            <v-icon left small>check</v-icon>
            Approve All
          </v-btn>
        </div>
      </div>
    </v-flex>

    <!-- Field Update Approved -->
    <v-flex v-else-if="valValue && valValue.fieldUpdate && valValue.fieldUpdate.status === 'approved'" xs12 class="body-1 py-1 text--primary">
      <div class="field-update-container approved">
        <div class="caption green--text mb-2">
          <v-icon small color="green" class="mr-1">check_circle</v-icon>
          Field Updates Applied Successfully
        </div>
        <div class="caption grey--text">
          {{ valValue.fieldUpdate.pendingUpdates.length }} field(s) were updated
        </div>
        <div class="pt-2">
          <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
            {{ lookup.buttonTitle || 'Generate' }}
          </v-btn>
        </div>
      </div>
    </v-flex>

    <!-- Field Update Rejected -->
    <v-flex v-else-if="valValue && valValue.fieldUpdate && valValue.fieldUpdate.status === 'rejected'" xs12 class="body-1 py-1 text--primary">
      <div class="field-update-container rejected">
        <div class="caption red--text mb-2">
          <v-icon small color="red" class="mr-1">cancel</v-icon>
          Field Updates Rejected
        </div>
        <div class="pt-2">
          <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
            {{ lookup.buttonTitle || 'Generate' }}
          </v-btn>
        </div>
      </div>
    </v-flex>

    <!-- Field Update Error -->
    <v-flex v-else-if="valValue && valValue.fieldUpdate && valValue.fieldUpdate.error" xs12 class="body-1 py-1 text--primary">
      <div class="field-update-container error-state">
        <div class="caption red--text mb-2">
          <v-icon small color="red" class="mr-1">error</v-icon>
          {{ valValue.fieldUpdate.error }}
        </div>
        <div v-if="valValue.fieldUpdate.rawResponse" class="caption grey--text" style="max-height: 100px; overflow-y: auto;">
          Raw response: {{ valValue.fieldUpdate.rawResponse }}
        </div>
        <div class="pt-2">
          <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
            {{ lookup.buttonTitle || 'Retry' }}
          </v-btn>
        </div>
      </div>
    </v-flex>

    <!-- Markdown Display -->
    <v-flex v-else-if="valValue && valValue.md" xs12 class="body-1 py-1 font-weight-bold text--primary" style="width: 100%; overflow-x: hidden">

      <div class="markdown-preview" v-html="renderedMarkdown"></div>

      <div class="pt-2 c-d-flex c-align-center justify-space-between">
        <v-btn icon flat :disabled="generating" small color="secondary" class="ma-0" @click="newTab">
          <v-icon>fullscreen</v-icon>
        </v-btn>
        <v-btn icon flat :disabled="generating" small color="secondary" class="ma-0" @click="copyContent">
          <v-icon>content_copy</v-icon>
        </v-btn>
        <v-btn icon flat :disabled="generating || downloadingPdf" :loading="downloadingPdf" small color="secondary" class="ma-0" @click="downloadAsPdf">
          <v-icon>picture_as_pdf</v-icon>
        </v-btn>
        <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
          {{ lookup.buttonTitle || 'Generate' }}
        </v-btn>
      </div>
    </v-flex>

    <!-- HTML Display -->
    <v-flex v-else-if="valValue && valValue.html" xs12 class="body-1 py-1 font-weight-bold text--primary" style="width: 100%; overflow-x: hidden">
      
      <div class="card-back" style="width: 100%; overflow-x: hidden">
        <iframe style="width: 100%; overflow-y: auto; overflow-x: hidden; aspect-ratio: 4/3; border-radius: 5px" frameborder="0" :srcdoc="valValue.html" ref="htmlContent"></iframe>
      </div>
      
      <div class="pt-2 px-2 c-d-flex c-align-center justify-space-between">
        <v-btn icon flat :disabled="generating" small color="secondary" class="ma-0" @click="newTab">
          <v-icon>fullscreen</v-icon>
        </v-btn>
        <v-btn icon flat :disabled="generating" small color="secondary" class="ma-0" @click="copyContent">
          <v-icon>content_copy</v-icon>
        </v-btn>
        <v-btn icon flat :disabled="generating || downloadingPdf" :loading="downloadingPdf" small color="secondary" class="ma-0" @click="downloadAsPdf">
          <v-icon>picture_as_pdf</v-icon>
        </v-btn>
        <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
          {{ lookup.buttonTitle || 'Generate' }}
        </v-btn>
      </div>




    </v-flex>
    <v-flex xs12 class="body-1 py-1 px-2 text--primary" v-else-if="valValue && valValue.error">
      
      <div class="py-2 text-xs-center red--text">
        Error: {{valValue.error}}
      </div>
      <div class="py-2 text-xs-center red--text">
        <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
          {{ lookup.buttonTitle || 'Generate' }}
        </v-btn>
      </div>
    </v-flex>
    <v-flex xs12 class="body-1 py-1 px-2 font-weight-bold text--primary c-d-flex c-justify-center" v-else-if="!stillGenerating">
      
      <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">
        {{ lookup.buttonTitle || 'Generate' }}
      </v-btn>
    </v-flex>
    <v-flex v-else-if="stillGenerating" xs12 class="body-1 py-1 font-weight-bold text--primary c-d-flex c-justify-center">
      
<!--      <v-btn :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="generate">-->
<!--        {{ lookup.buttonTitle || 'Generate' }}-->
<!--      </v-btn>-->
      
      <div class="c-flex-grow-1 text-xs-center">
        Generating...
      </div>
      
      <div>
        <v-btn flat icon :disabled="generating" :loading="generating" small color="secondary" class="ma-0" @click="cancelGenerate">
          <v-icon>cancel</v-icon>
        </v-btn>
      </div>
      
      
      
<!--      {{ valValue }}-->
    
    </v-flex>
    
    
    <!--        <v-flex shrink>-->
    <!--          -->
    <!--        -->
    <!--&lt;!&ndash;            <v-tooltip top>&ndash;&gt;-->
    <!--&lt;!&ndash;                <template v-slot:activator="{on}">&ndash;&gt;-->
    <!--&lt;!&ndash;                    <v-icon&ndash;&gt;-->
    <!--&lt;!&ndash;                          :color="!(binderField && binderField.status === 'Success') ? 'amber' : null"&ndash;&gt;-->
    <!--&lt;!&ndash;                          v-on="on"&ndash;&gt;-->
    <!--&lt;!&ndash;                          @click="binderField ? evalBinder.runField(binderField, true) : null"&ndash;&gt;-->
    <!--&lt;!&ndash;                    >&ndash;&gt;-->
    <!--&lt;!&ndash;                        {{binderField && binderField.status === 'Success' ? 'functions' : 'warning'}}&ndash;&gt;-->
    <!--&lt;!&ndash;                    </v-icon>&ndash;&gt;-->
    <!--&lt;!&ndash;                </template>&ndash;&gt;-->
    <!--&lt;!&ndash;                {{binderField && binderField.status === 'Success' ? 'Evaluation Success' : "Evaluation Failed"}}&ndash;&gt;-->
    <!--&lt;!&ndash;            </v-tooltip>&ndash;&gt;-->
    <!--        </v-flex>-->
    
    
    <!--        <v-flex xs12 class="text-xs-center" v-if="showLoading">-->
    <!--            <v-progress-circular-->
    <!--                    :size="28"-->
    <!--                    :width="3"-->
    <!--                    color="secondary"-->
    <!--                    indeterminate-->
    <!--            ></v-progress-circular>-->
    <!--        </v-flex>-->
    
    <!--        <template v-else>-->
    <!--            <v-flex xs12 v-if="!blockInput">-->
    <!--                <v-layout row align-center>-->
    <!--                    <v-flex xs12>-->
    <!--                        <v-text-field-->
    <!--                                clearable-->
    <!--                                color="primary"-->
    <!--                                flat-->
    <!--                                hide-details-->
    <!--                                class="pa-0 ma-0"-->
    <!--                                single-line-->
    <!--                                :label="value.description"-->
    <!--                                :disabled="!intToBool(value.editable) || !stepEditable"-->
    <!--                                @input="bindData = false"-->
    <!--                                :value="value.value"-->
    <!--                                @change="val => value.value = val">-->
    <!--                        </v-text-field>-->
    <!--                    </v-flex>-->
    <!--                    <v-flex shrink v-if="loadingMap">-->
    <!--                        <v-progress-circular-->
    <!--                                :size="20"-->
    <!--                                :width="2"-->
    <!--                                color="primary"-->
    <!--                                indeterminate-->
    <!--                        />-->
    <!--                    </v-flex>-->
    <!--                </v-layout>-->
    <!--            </v-flex>-->
    <!--            <v-flex xs12 v-else class="py-1">-->
    <!--                <v-layout row align-center>-->
    <!--                    <v-flex shrink>-->
    <!--                        <v-icon class="px-2 amber&#45;&#45;text">warning</v-icon>-->
    <!--                    </v-flex>-->
    <!--                    <v-flex>-->
    <!--                        <span class="body-2 greyType&#45;&#45;text">{{ fieldSetSelectField.description + ' Required' }}</span>-->
    <!--                    </v-flex>-->
    <!--                </v-layout>-->
    <!--            </v-flex>-->
    <!--        </template>-->
  
  
  </v-layout>
</template>

<script>

import {codeBaseMixin} from "../../../../../../codeBaseMixin";
import {mapGetters} from 'vuex';
import {blankString} from "../../../../../../codeFunctions";
import {DatasetBinder} from "@/Tasks/TaskView V2/components/TaskViewDetail/datasetBinder";
import {EvalBinder} from "@/Tasks/TaskView V2/components/TaskViewDetail/evalsBinder";
import {lookup} from "mime-types";
import MarkdownIt from 'markdown-it';
import markdownItKatex from 'markdown-it-katex';
import 'katex/dist/katex.min.css';
export default {
  mixins: [codeBaseMixin],
  props: {
    value: Object,
    selectFields: Array,
    fieldSets: Array,
    stepEditable: Boolean,
    linkedFieldInfo: Object,
    binder: DatasetBinder,
    evalBinder: EvalBinder,
    task: Object
  },
  
  data() {
    return {
      bindData: true,
      curReqID: -1,
      loadingMap: false,
      loading: false,
      generating: false,
      approving: false,
      downloadingPdf: false,
      recheck: null,
      console: console
    }
  },
  
  beforeDestroy() {
    try {
      clearTimeout(this.recheck)
    } catch (e) {
      console.log(e)
    }
  },
  watch: {
    valValue: {
      handler(val) {
        this.checkValue()
      },
      immediate: true,
      deep: true
    }
  },
  
  methods: {
    
    test() {
      
      console.log(field)
      
    },
    
    newTab() {
      // Check if we have markdown or HTML content
      const hasMarkdown = this.valValue?.md;
      const hasHtml = this.valValue?.html;

      if (!hasMarkdown && !hasHtml) {
        this.$snack.warning('No content to display');
        return;
      }

      // Build a complete HTML document with KaTeX CSS
      let htmlContent;

      if (hasMarkdown) {
        // For markdown, include KaTeX CSS and render the markdown
        htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css">
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      padding: 20px;
      max-width: 900px;
      margin: 0 auto;
      line-height: 1.6;
    }
    h1, h2, h3, h4, h5, h6 {
      margin-top: 24px;
      margin-bottom: 16px;
      font-weight: 600;
    }
    h1 {
      font-size: 2em;
      border-bottom: 1px solid #ddd;
      padding-bottom: 8px;
    }
    h2 {
      font-size: 1.5em;
      border-bottom: 1px solid #eee;
      padding-bottom: 6px;
    }
    h3 { font-size: 1.25em; }
    p { margin-bottom: 12px; }
    code {
      background-color: rgba(0, 0, 0, 0.05);
      padding: 2px 6px;
      border-radius: 3px;
      font-family: monospace;
      font-size: 0.9em;
    }
    pre {
      background-color: #2d2d2d;
      color: #f8f8f2;
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
      margin-bottom: 12px;
    }
    pre code {
      background-color: transparent;
      padding: 0;
      color: inherit;
    }
    ul, ol {
      margin-bottom: 12px;
      padding-left: 24px;
    }
    li { margin-bottom: 4px; }
    blockquote {
      border-left: 4px solid #ddd;
      padding-left: 16px;
      margin: 12px 0;
      color: #666;
      font-style: italic;
    }
    table {
      border-collapse: collapse;
      margin-bottom: 12px;
      width: 100%;
    }
    table th, table td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    table th {
      background-color: #f0f0f0;
      font-weight: 600;
    }
    a {
      color: #1976d2;
      text-decoration: none;
    }
    a:hover { text-decoration: underline; }
    img {
      max-width: 100%;
      height: auto;
    }
    hr {
      border: none;
      border-top: 1px solid #ddd;
      margin: 16px 0;
    }
  </style>
</head>
<body>
  ${this.renderedMarkdown}
</body>
</html>`;
      } else {
        htmlContent = hasHtml;
      }

      // Create a Blob from the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Open the blob URL in a new tab
      window.open(blobUrl, '_blank');

      // Clean up the blob URL after a short delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);
    },

    async downloadAsPdf() {
      try {
        this.downloadingPdf = true;
        const hasMarkdown = this.valValue?.md;
        const hasHtml = this.valValue?.html;
        if (!hasMarkdown && !hasHtml) {
          this.$snack.warning('No content to download');
          return;
        }
        const htmlBody = hasMarkdown || hasHtml;
        const doc = await this.$newReq('POST', 'task/ai/createPdfDoc', {
          taskId: this.task.taskID,
          fieldId: this.value.fieldID,
          htmlBody: htmlBody
        })
        
        this.task.structure.documents.push(doc)
        this.$snack.info('PDF added to task successfully');
      } catch (err) {
        console.error('PDF generation error:', err);
        this.$snack.error('Failed to generate PDF');
      } finally {
        this.downloadingPdf = false;
      }
    },

    copyContent() {
      // Check if we have markdown or HTML content
      const hasMarkdown = this.valValue?.md;
      const hasHtml = this.valValue?.html;

      if (!hasMarkdown && !hasHtml) {
        this.$snack.warning('No content to copy');
        return;
      }

      try {
        // Create a temporary element to hold the content
        const tempElement = document.createElement('div');
        tempElement.style.position = 'absolute';
        tempElement.style.left = '-9999px';

        if (hasMarkdown) {
          // Use the rendered markdown HTML
          tempElement.innerHTML = this.renderedMarkdown;
        } else {
          // Use the iframe content for HTML
          if (!this.$refs.htmlContent) {
            this.$snack.error('Content element not found');
            return;
          }
          tempElement.innerHTML = this.valValue.html;
        }

        document.body.appendChild(tempElement);

        // Select the content
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(tempElement);
        selection.removeAllRanges();
        selection.addRange(range);

        // Copy the selected content
        const success = document.execCommand('copy');

        // Clear selection and remove temp element
        selection.removeAllRanges();
        document.body.removeChild(tempElement);

        if (success) {
          this.$snack.info('Content copied to clipboard');
        } else {
          this.$snack.error('Failed to copy content');
        }
      } catch (err) {
        console.error('Copy error:', err);
        this.$snack.error('Failed to copy content');
      }
    },
    

    async generate() {
      try {
        this.generating = true
        console.log(this.task, this.value)
        const res = await this.$newReq('POST', 'task/ai/generate', {
          taskId: this.task.taskID,
          fieldId: this.value.fieldID
        })
        
        if (res.error) {
          this.$snack.error(res.error)
        }
        
        if (res.executionId) {
          this.value.value = JSON.stringify({executionId: res.executionId})
          this.$emit('saveFields')
          this.$snack.info('Generating...')
          this.checkValue()
        }
      } catch (e) {
        console.log(e)
        this.$snack.networkError()
      } finally {
        this.generating = false
      }
    },
    async checkValue() {
      if (this.stillGenerating) {
        try {
          const res = await this.$newReq('POST', 'task/ai/checkValue', {
            taskId: this.task.taskID,
            fieldId: this.value.fieldID
          })
          const field = this.task.structure.fields.find(field => field.fieldID === this.value.fieldID)
          
          if (res?.error || res?.success) {
            field.value = JSON.stringify(res)
            field.oldValue = JSON.stringify(res)
          }
          
          this.recheck = setTimeout(this.checkValue, 2500)
        } catch (e) {
          console.log(e)
          this.recheck = setTimeout(this.checkValue, 2500)
        }
      }
    },
    async cancelGenerate() {
      try {
        this.generating = true
        const res = await this.$newReq('POST', 'task/ai/cancelGenerate', {
          taskId: this.task.taskID,
          fieldId: this.value.fieldID,
          val: {executionId: this.valValue.executionId, error: 'Cancelled'}
        })
        const field = this.task.structure.fields.find(field => field.fieldID === this.value.fieldID)
        field.value = JSON.stringify({executionId: this.valValue.executionId, error: 'Cancelled'})
        field.oldValue = JSON.stringify({executionId: this.valValue.executionId, error: 'Cancelled'})
      } catch (e) {
        console.log(e)
        this.generating = false
      }
    },

    async approveUpdates() {
      try {
        this.approving = true

        for (const update of this.valValue.fieldUpdate.pendingUpdates) {
          const updateField = this.task.structure.fields.find(v => v.fieldID === update.fieldId)
          if (updateField) {
            updateField.value = update.value
          }
        }

        // Update this AI field's value to mark as approved
        const currentValue = { ...this.valValue }
        currentValue.fieldUpdate.status = 'approved'
        this.value.value = JSON.stringify(currentValue)
        
        await new Promise((resolve) => setTimeout(resolve, 500))
        
        // Emit save so the binder picks up all changes
        this.$emit('saveFields');

        this.$snack.info('Field updates applied')
      } catch (e) {
        console.log(e)
        this.$snack.error('Failed to approve field updates')
      } finally {
        this.approving = false
      }
    },

    async rejectUpdates() {
      try {
        this.approving = true

        const res = await this.$newReq('POST', 'task/ai/rejectFieldUpdates', {
          taskId: this.task.taskID,
          fieldId: this.value.fieldID
        })

        if (res.success) {
          this.$snack.info('Field updates rejected')

          // Update the AI field value to show rejected
          const currentValue = { ...this.valValue }
          currentValue.fieldUpdate.status = 'rejected'
          this.value.value = JSON.stringify(currentValue)
          this.value.oldValue = JSON.stringify(currentValue)
        }
      } catch (e) {
        console.log(e)
        this.$snack.error('Failed to reject field updates')
      } finally {
        this.approving = false
      }
    },

    formatValue(value, fieldType) {
      if (value == null) return '<em class="grey--text">No value</em>'

      if (fieldType === 'Note Field') {
        // Return HTML as-is for note fields
        return value
      }

      if (fieldType === 'Date Field') {
        // Format date nicely
        try {
          const date = new Date(value)
          return date.toLocaleString()
        } catch (e) {
          return value
        }
      }

      if (fieldType === 'Currency Field') {
        // Format as currency
        try {
          return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(value)
        } catch (e) {
          return value
        }
      }

      if (fieldType === 'Number Field') {
        // Format as number
        try {
          return new Intl.NumberFormat('en-ZA').format(value)
        } catch (e) {
          return value
        }
      }

      // Default: escape HTML and return
      return String(value).replace(/</g, '&lt;').replace(/>/g, '&gt;')
    },
  },
  
  
  
  
  computed: {
    lookup() {
      return JSON.parse(this.value.lookup)
    },
    valValue() {
      return this.value.value ? JSON.parse(this.value.value) : null
    },
    stillGenerating() {
      return this.valValue?.executionId && !this.valValue.success && !this.valValue.error && !this.generating
    },
    renderedMarkdown() {
      if (!this.valValue?.md) return '';
      const md = new MarkdownIt({
        html: true,
        linkify: true,
        typographer: true
      });
      md.use(markdownItKatex);
      return md.render(this.valValue.md);
    }
  }
  
}
</script>

<style scoped>
.field-update-container {
  padding: 12px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.field-update-container.approved {
  background: rgba(76, 175, 80, 0.08);
  border-color: rgba(76, 175, 80, 0.3);
}

.field-update-container.rejected {
  background: rgba(244, 67, 54, 0.08);
  border-color: rgba(244, 67, 54, 0.3);
}

.field-update-container.error-state {
  background: rgba(244, 67, 54, 0.05);
  border-color: rgba(244, 67, 54, 0.2);
}

.pending-updates-list {
  max-height: 300px;
  overflow-y: auto;
}

.pending-update-item {
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.proposed-value {
  background: rgba(0, 0, 0, 0.03);
  border-radius: 3px;
  max-height: 100px;
  overflow-y: auto;
  font-family: inherit;
}

.markdown-preview {
  padding: 12px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: auto;
}

.markdown-preview >>> h1,
.markdown-preview >>> h2,
.markdown-preview >>> h3,
.markdown-preview >>> h4,
.markdown-preview >>> h5,
.markdown-preview >>> h6 {
  margin-top: 16px;
  margin-bottom: 8px;
  font-weight: 600;
}

.markdown-preview >>> h1 {
  font-size: 2em;
  border-bottom: 1px solid #ddd;
  padding-bottom: 8px;
}

.markdown-preview >>> h2 {
  font-size: 1.5em;
  border-bottom: 1px solid #eee;
  padding-bottom: 6px;
}

.markdown-preview >>> h3 {
  font-size: 1.25em;
}

.markdown-preview >>> p {
  margin-bottom: 12px;
  line-height: 1.6;
}

.markdown-preview >>> code {
  background-color: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.9em;
}

.markdown-preview >>> pre {
  background-color: #2d2d2d;
  color: #f8f8f2;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 12px;
}

.markdown-preview >>> pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.markdown-preview >>> ul,
.markdown-preview >>> ol {
  margin-bottom: 12px;
  padding-left: 24px;
}

.markdown-preview >>> li {
  margin-bottom: 4px;
}

.markdown-preview >>> blockquote {
  border-left: 4px solid #ddd;
  padding-left: 16px;
  margin: 12px 0;
  color: #666;
  font-style: italic;
}

.markdown-preview >>> table {
  border-collapse: collapse;
  margin-bottom: 12px;
  width: 100%;
}

.markdown-preview >>> table th,
.markdown-preview >>> table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.markdown-preview >>> table th {
  background-color: #f0f0f0;
  font-weight: 600;
}

.markdown-preview >>> a {
  color: #1976d2;
  text-decoration: none;
}

.markdown-preview >>> a:hover {
  text-decoration: underline;
}

.markdown-preview >>> img {
  max-width: 100%;
  height: auto;
}

.markdown-preview >>> hr {
  border: none;
  border-top: 1px solid #ddd;
  margin: 16px 0;
}
</style>
